/**
 * Centralized Currency & Tax Configuration
 * Single source of truth for multi-region support
 */

export const CURRENCIES = [
  { 
    value: 'AED', 
    label: 'AED (د.إ) - UAE Dirham', 
    symbol: 'د.إ',
    defaultTaxType: 'vat',
    defaultTaxRate: 5.0  // UAE VAT is 5%
  },
  { 
    value: 'USD', 
    label: 'USD ($) - US Dollar', 
    symbol: '$',
    defaultTaxType: 'sales_tax',
    defaultTaxRate: 8.25  // Average US sales tax
  },
  { 
    value: 'EUR', 
    label: 'EUR (€) - Euro', 
    symbol: '€',
    defaultTaxType: 'vat',
    defaultTaxRate: 20.0  // EU standard VAT
  },
  { 
    value: 'GBP', 
    label: 'GBP (£) - British Pound', 
    symbol: '£',
    defaultTaxType: 'vat',
    defaultTaxRate: 20.0  // UK VAT
  }
];

export const TAX_TYPES = [
  { 
    value: 'vat', 
    label: 'VAT (Value Added Tax)',
    description: 'Used in UAE, EU, UK',
    commonRates: [5, 15, 20]  // Common VAT rates
  },
  { 
    value: 'sales_tax', 
    label: 'Sales Tax',
    description: 'Used in USA',
    commonRates: [0, 6, 7, 8, 8.25, 9, 10]  // Common US state rates
  },
  { 
    value: 'gst', 
    label: 'GST (Goods & Services Tax)',
    description: 'Used in India, Singapore',
    commonRates: [5, 7, 18]  // Common GST rates
  },
  { 
    value: 'none', 
    label: 'No Tax',
    description: 'Tax-free regions',
    commonRates: [0]
  }
];

/**
 * Get currency symbol from currency code
 * @param {string} currencyCode - Currency code (e.g., 'AED')
 * @returns {string} Symbol (e.g., 'د.إ')
 */
export const getCurrencySymbol = (currencyCode) => {
  const currency = CURRENCIES.find(c => c.value === currencyCode);
  return currency?.symbol || '$';
};

/**
 * Get currency label from currency code
 * @param {string} currencyCode - Currency code (e.g., 'AED')
 * @returns {string} Label (e.g., 'AED (د.إ) - UAE Dirham')
 */
export const getCurrencyLabel = (currencyCode) => {
  const currency = CURRENCIES.find(c => c.value === currencyCode);
  return currency?.label || 'USD ($) - US Dollar';
};

/**
 * Get default tax type for a currency
 * @param {string} currencyCode - Currency code (e.g., 'AED')
 * @returns {string} Tax type ('vat' | 'sales_tax' | 'gst' | 'none')
 */
export const getDefaultTaxType = (currencyCode) => {
  const currency = CURRENCIES.find(c => c.value === currencyCode);
  return currency?.defaultTaxType || 'sales_tax';
};

/**
 * Get default tax rate for a currency
 * @param {string} currencyCode - Currency code (e.g., 'AED')
 * @returns {number} Tax rate percentage
 */
export const getDefaultTaxRate = (currencyCode) => {
  const currency = CURRENCIES.find(c => c.value === currencyCode);
  return currency?.defaultTaxRate || 0;
};

/**
 * Format price with currency symbol
 * @param {number} amount - Price amount
 * @param {string} currencyCode - Currency code (e.g., 'AED')
 * @returns {string} Formatted price (e.g., 'د.إ2,550')
 */
export const formatPrice = (amount, currencyCode = 'USD') => {
  const symbol = getCurrencySymbol(currencyCode);
  const formatted = amount.toLocaleString('en-US', { 
    minimumFractionDigits: 2, 
    maximumFractionDigits: 2 
  });
  
  // For Arabic currencies (AED), put symbol before
  // For Western currencies (USD, EUR, GBP), put symbol before
  return `${symbol}${formatted}`;
};

/**
 * Default currency (AED for primary market - UAE)
 */
export const DEFAULT_CURRENCY = 'AED';

/**
 * Default tax type (VAT for UAE)
 */
export const DEFAULT_TAX_TYPE = 'vat';

/**
 * Default tax rate (5% for UAE VAT)
 */
export const DEFAULT_TAX_RATE = 5.0;
